import axios from 'axios';

// Track last tracked URL and timestamp to prevent duplicates
let lastTrackedUrl = null;
let lastTrackedTime = 0;
const TRACKING_COOLDOWN = 2000; // 2 seconds cooldown to prevent duplicate tracking

// Track page visit
export const trackPageVisit = async (pageName, url, duration = null) => {
  try {
    // Skip tracking if pageName is null (page not in allowed list)
    if (!pageName) {
      return;
    }

    // Check if user is logged in via localStorage
    const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
    
    if (!isLoggedIn) {
      // User not authenticated, skip tracking
      return;
    }

    // Prevent duplicate tracking: if same URL was tracked recently, skip
    const now = Date.now();
    if (lastTrackedUrl === url && (now - lastTrackedTime) < TRACKING_COOLDOWN) {
      // Same URL tracked recently, skip to prevent duplicates
      if (process.env.NODE_ENV === 'development') {
        console.log('⏭️ Skipping duplicate tracking:', { page: pageName, url });
      }
      return;
    }

    // Note: Token might be httpOnly, so we can't check it in document.cookie
    // But withCredentials: true will send it automatically if it exists
    
    // REACT_APP_BASE_URL already includes /api, so just add the endpoint
    const trackingUrl = `${process.env.REACT_APP_BASE_URL}/user-activity/track`;
    
    const response = await axios.post(
      trackingUrl,
      {
        page: pageName,
        url: url,
        duration: duration,
      },
      {
        withCredentials: true,
      }
    );

    // Update last tracked URL and time
    lastTrackedUrl = url;
    lastTrackedTime = now;

    // Log success in development
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Activity tracked:', { page: pageName, url });
    }
  } catch (error) {
    // Log error but don't interrupt user experience
    console.error('❌ Error tracking page visit:', {
      page: pageName,
      url: url,
      error: error.response?.data || error.message,
      status: error.response?.status,
    });
  }
};

// Helper function to check if a page should be tracked
export const shouldTrackPage = (pathname) => {
  // Only track specific pages:
  // - Seller details: /sellers/:sellerId
  // - Logistics details: /logistics/:id
  // - Business details: /business/:id
  // - Franchise details: /franchise/:franchiseId
  // - Service details: /services/:id
  // - Single product page: /products/:productid
  // - Subscription page: /subscriptions or /Subscriptions
  // - Tenders page: /tenders or /tenders/faq
  
  return (
    pathname.startsWith('/sellers/') ||
    pathname.startsWith('/logistics/') ||
    pathname.startsWith('/business/') ||
    pathname.startsWith('/franchise/') ||
    pathname.startsWith('/services/') ||
    pathname.startsWith('/products/') ||
    pathname === '/subscriptions' ||
    pathname === '/Subscriptions' ||
    pathname === '/tenders' ||
    pathname.startsWith('/tenders/')
  );
};

// Get page name from URL path (only for tracked pages)
// Returns simple format: "Franchise: FR-17OJOM" or "Seller: {id}" or "Subscription" or "Tenders"
export const getPageNameFromPath = (pathname) => {
  // Only return page names for pages that should be tracked
  if (!shouldTrackPage(pathname)) {
    return null; // Return null to skip tracking
  }

  // Extract entity ID and return simple format: "Type: ID"
  if (pathname.startsWith('/sellers/')) {
    const sellerId = pathname.split('/sellers/')[1]?.split('/')[0];
    return sellerId ? `Seller: ${sellerId}` : null;
  }
  
  if (pathname.startsWith('/logistics/')) {
    const logisticsId = pathname.split('/logistics/')[1]?.split('/')[0];
    return logisticsId ? `Logistics: ${logisticsId}` : null;
  }
  
  if (pathname.startsWith('/business/')) {
    const businessId = pathname.split('/business/')[1]?.split('/')[0];
    return businessId ? `Business: ${businessId}` : null;
  }
  
  if (pathname.startsWith('/franchise/')) {
    const franchiseId = pathname.split('/franchise/')[1]?.split('/')[0];
    return franchiseId ? `Franchise: ${franchiseId}` : null;
  }
  
  if (pathname.startsWith('/services/')) {
    const serviceId = pathname.split('/services/')[1]?.split('/')[0];
    return serviceId ? `Service: ${serviceId}` : null;
  }
  
  if (pathname.startsWith('/products/')) {
    const productId = pathname.split('/products/')[1]?.split('/')[0];
    return productId ? `Product: ${productId}` : null;
  }

  // Subscription pages
  if (pathname === '/subscriptions' || pathname === '/Subscriptions') {
    return 'Subscription';
  }

  // Tenders pages
  if (pathname === '/tenders') {
    return 'Tenders';
  }
  
  if (pathname.startsWith('/tenders/')) {
    const subPath = pathname.split('/tenders/')[1]?.split('/')[0];
    if (subPath === 'faq') {
      return 'Tenders FAQ';
    }
    // If there's a tender ID, return it
    return subPath ? `Tender: ${subPath}` : 'Tenders';
  }

  return null;
};




